import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Switch } from '../components/ui/switch';
import {
  ClipboardCheck, ChevronLeft, ChevronRight, Plus, CheckCircle2, XCircle,
  Clock, AlertTriangle, Check, Trash2, Copy, ClipboardPaste, Save, FileDown
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getISOWeekString = (date) => {
  const target = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = target.getUTCDay() || 7;
  target.setUTCDate(target.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
  const weekNum = Math.ceil(((target - yearStart) / 86400000 + 1) / 7);
  return `${target.getUTCFullYear()}-W${String(weekNum).padStart(2, '0')}`;
};

const getCurrentWeek = () => getISOWeekString(new Date());

const getWeekDates = (weekStr) => {
  try {
    const [year, week] = weekStr.split('-W').length === 2
      ? [parseInt(weekStr.split('-W')[0]), parseInt(weekStr.split('-W')[1])]
      : [parseInt(weekStr.split('-')[0]), parseInt(weekStr.split('W')[1] || weekStr.split('-')[1])];
    const jan4 = new Date(year, 0, 4);
    const dayOfWeek = jan4.getDay() || 7;
    const monday = new Date(jan4);
    monday.setDate(jan4.getDate() - dayOfWeek + 1 + (week - 1) * 7);
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      dates.push(`${yyyy}-${mm}-${dd}`);
    }
    return dates;
  } catch {
    return [];
  }
};

const getAdjacentWeek = (weekStr, direction) => {
  const dates = getWeekDates(weekStr);
  if (!dates.length) return weekStr;
  const [y, m, d] = dates[0].split('-').map(Number);
  const ref = new Date(y, m - 1, d + direction * 7, 12, 0, 0);
  return getISOWeekString(ref);
};

const joursSemaine = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

const statutOptions = [
  { value: 'present', label: 'Présent', color: 'bg-green-100 text-green-800 border-green-200' },
  { value: 'absent', label: 'Absent', color: 'bg-red-100 text-red-800 border-red-200' },
  { value: 'retard', label: 'Retard', color: 'bg-amber-100 text-amber-800 border-amber-200' },
  { value: 'absent_justifie', label: 'Absent justifié', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  { value: 'conge', label: 'Congé', color: 'bg-purple-100 text-purple-800 border-purple-200' }
];

const calcEffective = (debut, fin, pauseMin) => {
  if (!debut || !fin) return 0;
  const [h1, m1] = debut.split(':').map(Number);
  const [h2, m2] = fin.split(':').map(Number);
  const totalMin = (h2 * 60 + m2) - (h1 * 60 + m1) - (pauseMin || 0);
  return Math.max(0, Math.round((totalMin / 60) * 100) / 100);
};

const formatHours = (h) => {
  if (!h || h === 0) return '0h';
  const hours = Math.floor(h);
  const minutes = Math.round((h - hours) * 60);
  return minutes > 0 ? `${hours}h${String(minutes).padStart(2, '0')}` : `${hours}h`;
};

export default function Emargement() {
  const { user } = useAuth();
  const canEdit = ['admin', 'rh', 'super_admin'].includes(user?.role);

  const [data, setData] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [absenceTypes, setAbsenceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(getCurrentWeek());
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [departments, setDepartments] = useState([]);

  // Popover editing
  const [editingCell, setEditingCell] = useState(null);
  const [cellForm, setCellForm] = useState({
    statut: 'present', heure_arrivee: '08:00', heure_depart: '17:00',
    type_absence: '', commentaire: ''
  });

  // Clipboard & drag
  const [clipboardSlot, setClipboardSlot] = useState(null);
  const [dragSource, setDragSource] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  const weekDates = getWeekDates(selectedWeek);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const dept = filterDepartment === 'all' ? undefined : filterDepartment;
      const [semaineRes, empRes, absTypesRes] = await Promise.all([
        axios.get(`${API_URL}/emargement/semaine/${selectedWeek}`, { params: { departement: dept } }),
        axios.get(`${API_URL}/employees`),
        axios.get(`${API_URL}/admin/absence-types`).catch(() => ({ data: [] }))
      ]);
      setData(semaineRes.data);
      const activeEmps = empRes.data.filter(e => e.statut === 'actif');
      setEmployees(activeEmps);
      setAbsenceTypes(absTypesRes.data.filter(t => t.actif !== false));
      const deptList = activeEmps.map(e => e.departement).filter((v, i, a) => v && a.indexOf(v) === i);
      setDepartments(deptList);
    } catch {
      toast.error('Erreur lors du chargement des émargements');
    } finally {
      setLoading(false);
    }
  }, [selectedWeek, filterDepartment]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setClipboardSlot(null); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const changeWeek = (direction) => {
    setSelectedWeek(getAdjacentWeek(selectedWeek, direction));
  };

  // Helpers
  const getEmpData = (empId) => data.find(d => d.employee_id === empId);
  const getEmargement = (empId, date) => getEmpData(empId)?.emargements?.find(e => e.date === date);
  const getPlanned = (empId, date) => getEmpData(empId)?.planning_creneaux?.find(c => c.date === date);
  const isValidated = (empId) => getEmpData(empId)?.validation?.valide === true;

  const getStatutStyle = (statut) => {
    return statutOptions.find(s => s.value === statut)?.color || 'bg-slate-100 text-slate-600 border-slate-200';
  };
  const getStatutLabel = (statut) => {
    return statutOptions.find(s => s.value === statut)?.label || statut;
  };

  // --- Cell Actions ---

  const openCellEditor = (employeeId, date) => {
    if (!canEdit || isValidated(employeeId)) return;
    const em = getEmargement(employeeId, date);
    const planned = getPlanned(employeeId, date);
    if (em) {
      setCellForm({
        statut: em.statut || 'present',
        heure_arrivee: em.heure_arrivee || '08:00',
        heure_depart: em.heure_depart || '17:00',
        pause_minutes: em.pause_minutes ?? 0,
        type_absence: em.type_absence || '',
        commentaire: em.commentaire || ''
      });
    } else if (planned) {
      setCellForm({
        statut: 'present',
        heure_arrivee: planned.heure_debut || '08:00',
        heure_depart: planned.heure_fin || '17:00',
        pause_minutes: planned.pause_minutes ?? 0,
        type_absence: '',
        commentaire: ''
      });
    } else {
      setCellForm({ statut: 'present', heure_arrivee: '08:00', heure_depart: '17:00', pause_minutes: 0, type_absence: '', commentaire: '' });
    }
    setEditingCell({ employeeId, date });
  };

  const handleSaveSlot = async () => {
    if (!editingCell) return;
    const { employeeId, date } = editingCell;
    const existing = getEmargement(employeeId, date);
    setSaving(true);
    try {
      const payload = {
        heure_arrivee: (cellForm.statut === 'present' || cellForm.statut === 'retard') ? cellForm.heure_arrivee : null,
        heure_depart: (cellForm.statut === 'present' || cellForm.statut === 'retard') ? cellForm.heure_depart : null,
        pause_minutes: (cellForm.statut === 'present' || cellForm.statut === 'retard') ? (cellForm.pause_minutes || 0) : 0,
        statut: cellForm.statut,
        type_absence: (cellForm.statut === 'absent_justifie' || cellForm.statut === 'conge') ? cellForm.type_absence : null,
        commentaire: cellForm.commentaire || null
      };
      if (existing) {
        await axios.put(`${API_URL}/emargement/${existing.id}`, payload);
      } else {
        await axios.post(`${API_URL}/emargement`, { employee_id: employeeId, date, ...payload });
      }
      setEditingCell(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSlot = async () => {
    if (!editingCell) return;
    const existing = getEmargement(editingCell.employeeId, editingCell.date);
    if (!existing) return;
    setSaving(true);
    try {
      await axios.delete(`${API_URL}/emargement/${existing.id}`);
      setEditingCell(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleCopySlot = () => {
    if (!editingCell) return;
    const em = getEmargement(editingCell.employeeId, editingCell.date);
    if (em) {
      setClipboardSlot({
        statut: em.statut, heure_arrivee: em.heure_arrivee, heure_depart: em.heure_depart,
        pause_minutes: em.pause_minutes || 0, type_absence: em.type_absence, commentaire: em.commentaire
      });
      toast.success('Émargement copié');
    }
    setEditingCell(null);
  };

  const handlePaste = async (employeeId, date) => {
    if (!clipboardSlot || !canEdit || isValidated(employeeId)) return;
    const existing = getEmargement(employeeId, date);
    if (existing) {
      toast.error('Cette cellule est déjà occupée');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/emargement`, { employee_id: employeeId, date, ...clipboardSlot });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du collage');
    } finally {
      setSaving(false);
    }
  };

  // --- Switch: copy planning to emargement in one click ---
  const handleSlideFromPlanning = async (employeeId, date) => {
    if (!canEdit || isValidated(employeeId)) return;
    const planned = getPlanned(employeeId, date);
    if (!planned) return;
    setSaving(true);
    try {
      await axios.post(`${API_URL}/emargement`, {
        employee_id: employeeId,
        date,
        heure_arrivee: planned.heure_debut || '08:00',
        heure_depart: planned.heure_fin || '17:00',
        pause_minutes: planned.pause_minutes || 0,
        statut: 'present',
        planning_id: planned.planning_id || null
      });
      toast.success('Émargement créé depuis planning');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  // --- Drag & Drop ---
  const handleDragStart = (e, empId, date) => {
    if (isValidated(empId)) return;
    setDragSource({ employeeId: empId, date });
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDragOver = (e, empId, date) => {
    e.preventDefault();
    setDragOver({ employeeId: empId, date });
  };

  const handleDragLeave = () => setDragOver(null);

  const handleDrop = async (e, targetEmpId, targetDate) => {
    e.preventDefault();
    setDragOver(null);
    if (!dragSource) return;
    if (dragSource.employeeId === targetEmpId && dragSource.date === targetDate) { setDragSource(null); return; }
    if (isValidated(targetEmpId)) { setDragSource(null); return; }

    const sourceEm = getEmargement(dragSource.employeeId, dragSource.date);
    if (!sourceEm) { setDragSource(null); return; }

    const targetEm = getEmargement(targetEmpId, targetDate);
    if (targetEm) {
      toast.error('Cette cellule est déjà occupée');
      setDragSource(null);
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API_URL}/emargement`, {
        employee_id: targetEmpId, date: targetDate,
        heure_arrivee: sourceEm.heure_arrivee, heure_depart: sourceEm.heure_depart,
        pause_minutes: sourceEm.pause_minutes || 0,
        statut: sourceEm.statut, type_absence: sourceEm.type_absence,
        commentaire: sourceEm.commentaire
      });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la copie');
      fetchData();
    } finally {
      setSaving(false);
      setDragSource(null);
    }
  };

  // --- Duplicate week ---
  const handleDuplicateWeek = async (direction) => {
    const sourceWeek = getAdjacentWeek(selectedWeek, direction === 'prev' ? -1 : 1);
    try {
      const res = await axios.get(`${API_URL}/emargement/semaine/${sourceWeek}`);
      const sourceData = res.data;
      const sourceDates = getWeekDates(sourceWeek);
      const targetDates = weekDates;
      const dateMap = {};
      sourceDates.forEach((sd, i) => { if (targetDates[i]) dateMap[sd] = targetDates[i]; });

      const emargements = [];
      for (const empData of sourceData) {
        for (const em of (empData.emargements || [])) {
          const newDate = dateMap[em.date];
          if (!newDate) continue;
          const existingInTarget = getEmargement(empData.employee_id, newDate);
          if (existingInTarget) continue;
          emargements.push({
            employee_id: empData.employee_id, date: newDate,
            heure_arrivee: em.heure_arrivee, heure_depart: em.heure_depart,
            pause_minutes: em.pause_minutes || 0,
            statut: em.statut, type_absence: em.type_absence,
            commentaire: em.commentaire
          });
        }
      }
      if (emargements.length === 0) {
        toast.error(`Aucun émargement à dupliquer depuis la semaine ${sourceWeek.split('-W')[1]}`);
        return;
      }
      const result = await axios.post(`${API_URL}/emargement/batch`, { emargements });
      toast.success(`${result.data.created} émargements dupliqués depuis S${sourceWeek.split('-W')[1]}`);
      fetchData();
    } catch {
      toast.error('Erreur lors de la duplication');
    }
  };

  // --- Generate from planning ---
  const handleGenerateFromPlanning = async () => {
    try {
      const planningsRes = await axios.get(`${API_URL}/planning`, { params: { semaine: selectedWeek } });
      const plannings = planningsRes.data.filter(p => p.statut === 'publie');
      if (plannings.length === 0) { toast.error('Aucun planning publié pour cette semaine'); return; }
      const emargements = [];
      for (const p of plannings) {
        for (const c of (p.creneaux || [])) {
          if (c.type === 'absence') continue;
          emargements.push({
            employee_id: c.employee_id, date: c.date,
            heure_arrivee: c.heure_debut, heure_depart: c.heure_fin,
            pause_minutes: c.pause_minutes || 0,
            statut: 'present', planning_id: p.id
          });
        }
      }
      if (emargements.length === 0) { toast.info('Aucun créneau à convertir'); return; }
      const result = await axios.post(`${API_URL}/emargement/batch`, { emargements });
      toast.success(`${result.data.created} émargements créés depuis le planning`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  // --- Validation ---
  const handleValidateWeek = async (employeeId, valide) => {
    try {
      await axios.post(`${API_URL}/emargement/validate`, {
        employee_id: employeeId, semaine: selectedWeek, valide
      });
      toast.success(valide ? 'Semaine validée' : 'Validation annulée');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  // --- Stats ---
  const totalPresents = data.reduce((s, d) => s + (d.nb_presents || 0), 0);
  const totalAbsents = data.reduce((s, d) => s + (d.nb_absents || 0), 0);
  const totalRetards = data.reduce((s, d) => s + (d.nb_retards || 0), 0);
  const totalValidated = data.filter(d => d.validation?.valide).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F5156]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ClipboardCheck className="h-6 w-6 text-[#0F5156]" />
            Émargement - Semaine {selectedWeek.split('-W')[1]}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {weekDates.length >= 2 && `Du ${weekDates[0]} au ${weekDates[6]}`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {saving && <span className="text-sm text-slate-400 flex items-center gap-1"><Save className="h-3 w-3 animate-pulse" /> Sauvegarde...</span>}
        </div>
      </div>

      {/* Navigation + Actions */}
      <Card>
        <CardContent className="py-3">
          <div className="flex flex-col sm:flex-row items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => changeWeek(-1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Input type="week" value={selectedWeek} onChange={e => { if (e.target.value) setSelectedWeek(e.target.value); }} max="2099-W52" className="w-44" />
              <Button variant="outline" size="sm" onClick={() => changeWeek(1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            <Select value={filterDepartment} onValueChange={setFilterDepartment}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Département" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les départements</SelectItem>
                {departments.map(d => (
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {canEdit && (
              <div className="flex items-center gap-2 ml-auto">
                <Button variant="outline" size="sm" onClick={() => handleDuplicateWeek('prev')} title="Dupliquer depuis la semaine précédente">
                  <Copy className="h-4 w-4 mr-1" /> S. précédente
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleDuplicateWeek('next')} title="Dupliquer depuis la semaine suivante">
                  <Copy className="h-4 w-4 mr-1" /> S. suivante
                </Button>
                <Button variant="outline" size="sm" onClick={handleGenerateFromPlanning}>
                  <FileDown className="h-4 w-4 mr-1" /> Depuis planning
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Clipboard indicator */}
      {clipboardSlot && (
        <div className="bg-violet-50 border border-violet-200 rounded-lg px-4 py-2 text-sm text-violet-700 flex items-center gap-2">
          <ClipboardPaste className="h-4 w-4" />
          Émargement copié — cliquez sur l'icône coller d'une cellule vide (Échap pour annuler)
          <Button variant="ghost" size="sm" onClick={() => setClipboardSlot(null)} className="ml-auto text-violet-500 hover:text-violet-700 h-6 px-2">
            Annuler
          </Button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="py-3 text-center">
            <div className="text-2xl font-bold text-green-600">{totalPresents}</div>
            <div className="text-xs text-slate-500">Présences</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-3 text-center">
            <div className="text-2xl font-bold text-red-600">{totalAbsents}</div>
            <div className="text-xs text-slate-500">Absences</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-3 text-center">
            <div className="text-2xl font-bold text-amber-600">{totalRetards}</div>
            <div className="text-xs text-slate-500">Retards</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-3 text-center">
            <div className="text-2xl font-bold text-[#0F5156]">{totalValidated}/{data.length}</div>
            <div className="text-xs text-slate-500">Validés</div>
          </CardContent>
        </Card>
      </div>

      {/* Grid */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-slate-50">
                <th className="sticky left-0 bg-slate-50 z-10 px-3 py-2 text-left font-semibold min-w-[160px]">Employé</th>
                {weekDates.map((date, i) => (
                  <th key={date} className="px-1 py-2 text-center font-semibold min-w-[120px]">
                    <div>{joursSemaine[i]}</div>
                    <div className="text-xs text-slate-400 font-normal">{date.slice(5)}</div>
                  </th>
                ))}
                <th className="px-2 py-2 text-center font-semibold min-w-[80px] bg-slate-100">Heures</th>
                <th className="px-2 py-2 text-center font-semibold min-w-[100px] bg-slate-100">Validation</th>
              </tr>
            </thead>
            <tbody>
              {data.length === 0 ? (
                <tr>
                  <td colSpan={weekDates.length + 3} className="text-center py-8 text-slate-400">
                    Aucune donnée pour cette semaine
                  </td>
                </tr>
              ) : data.map(empData => {
                const empValidated = empData.validation?.valide === true;

                return (
                  <tr key={empData.employee_id} className={`border-b hover:bg-slate-50/50 ${empValidated ? 'bg-green-50/30' : ''}`}>
                    <td className="sticky left-0 bg-white z-10 px-3 py-1 font-medium text-slate-700 border-r">
                      <div>{empData.employee_name}</div>
                      <div className="text-xs text-slate-400">{empData.poste || empData.departement || ''}</div>
                    </td>
                    {weekDates.map(date => {
                      const em = (empData.emargements || []).find(e => e.date === date);
                      const planned = (empData.planning_creneaux || []).find(c => c.date === date);
                      const isEditing = editingCell?.employeeId === empData.employee_id && editingCell?.date === date;
                      const isDragTarget = dragOver?.employeeId === empData.employee_id && dragOver?.date === date;

                      return (
                        <td
                          key={date}
                          className={`px-1 py-1 text-center align-top border-r transition-colors ${isDragTarget ? 'bg-blue-50 ring-2 ring-blue-300 ring-inset' : ''}`}
                          onDragOver={(e) => handleDragOver(e, empData.employee_id, date)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, empData.employee_id, date)}
                        >
                          {em ? (
                            /* --- Cell with emargement (+ optional planning comparison) --- */
                            <Popover open={isEditing} onOpenChange={(open) => { if (!open) setEditingCell(null); }}>
                              <PopoverTrigger asChild>
                                <div
                                  draggable={canEdit && !empValidated}
                                  onDragStart={(e) => handleDragStart(e, empData.employee_id, date)}
                                  onClick={() => openCellEditor(empData.employee_id, date)}
                                  className={`cursor-pointer rounded px-1 py-1 text-xs transition-all border ${getStatutStyle(em.statut)} ${canEdit && !empValidated ? 'hover:ring-2 hover:ring-[#0F5156]/30' : ''}`}
                                >
                                  {/* Planning line (comparison) */}
                                  {planned && planned.type !== 'absence' && (
                                    <div className="text-[9px] text-slate-400 border-b border-slate-200 pb-0.5 mb-0.5">
                                      <Clock className="h-2.5 w-2.5 inline mr-0.5" />
                                      {planned.heure_debut}-{planned.heure_fin}
                                    </div>
                                  )}
                                  {/* Emargement line */}
                                  <div className="font-medium">{getStatutLabel(em.statut)}</div>
                                  {(em.statut === 'present' || em.statut === 'retard') && em.heure_arrivee && em.heure_depart && (
                                    <div className="text-[10px]">{em.heure_arrivee}-{em.heure_depart}</div>
                                  )}
                                  {(em.statut === 'present' || em.statut === 'retard') && em.heure_arrivee && em.heure_depart && (
                                    <div className="text-[10px] text-slate-500">{formatHours(calcEffective(em.heure_arrivee, em.heure_depart, em.pause_minutes))}</div>
                                  )}
                                </div>
                              </PopoverTrigger>
                              {isEditing && (
                                <PopoverContent className="w-80 p-3" align="start">
                                  <EmargementSlotEditor
                                    form={cellForm}
                                    setForm={setCellForm}
                                    onSave={handleSaveSlot}
                                    onDelete={handleDeleteSlot}
                                    onCopy={handleCopySlot}
                                    isExisting={true}
                                    absenceTypes={absenceTypes}
                                    planned={planned}
                                  />
                                </PopoverContent>
                              )}
                            </Popover>
                          ) : planned ? (
                            /* --- Cell with planning only (no emargement) → show switch --- */
                            <Popover open={isEditing} onOpenChange={(open) => { if (!open) setEditingCell(null); }}>
                              <PopoverTrigger asChild>
                                <div
                                  onClick={(e) => {
                                    if (clipboardSlot && canEdit && !empValidated) {
                                      handlePaste(empData.employee_id, date);
                                    } else {
                                      openCellEditor(empData.employee_id, date);
                                    }
                                  }}
                                  className={`rounded px-1 py-1 text-xs transition-all border border-slate-200 bg-slate-50 ${canEdit && !empValidated ? 'cursor-pointer hover:ring-2 hover:ring-[#0F5156]/30' : ''} ${clipboardSlot && canEdit && !empValidated ? 'bg-violet-50 border-violet-200' : ''}`}
                                >
                                  {/* Planning info */}
                                  {planned.type !== 'absence' ? (
                                    <>
                                      <div className="text-[10px] text-slate-400">
                                        <Clock className="h-2.5 w-2.5 inline mr-0.5" />
                                        {planned.heure_debut}-{planned.heure_fin}
                                      </div>
                                      {/* Quick slide switch */}
                                      {canEdit && !empValidated && !clipboardSlot && (
                                        <div
                                          className="mt-1 flex items-center justify-center gap-1"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <Switch
                                            checked={false}
                                            onCheckedChange={() => handleSlideFromPlanning(empData.employee_id, date)}
                                            className="scale-75"
                                          />
                                          <span className="text-[9px] text-slate-400">Valider</span>
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <div className="text-[10px] text-orange-400">
                                      {planned.type_absence || 'Absence'}
                                    </div>
                                  )}
                                </div>
                              </PopoverTrigger>
                              {isEditing && !clipboardSlot && (
                                <PopoverContent className="w-80 p-3" align="start">
                                  <EmargementSlotEditor
                                    form={cellForm}
                                    setForm={setCellForm}
                                    onSave={handleSaveSlot}
                                    onDelete={null}
                                    onCopy={null}
                                    isExisting={false}
                                    absenceTypes={absenceTypes}
                                    planned={planned}
                                  />
                                </PopoverContent>
                              )}
                            </Popover>
                          ) : (
                            /* --- Empty cell --- */
                            <Popover open={isEditing} onOpenChange={(open) => { if (!open) setEditingCell(null); }}>
                              <PopoverTrigger asChild>
                                <div
                                  onClick={() => openCellEditor(empData.employee_id, date)}
                                  onDragOver={(e) => handleDragOver(e, empData.employee_id, date)}
                                  onDragLeave={handleDragLeave}
                                  onDrop={(e) => handleDrop(e, empData.employee_id, date)}
                                  className={`h-10 flex items-center justify-center rounded cursor-pointer transition-all ${
                                    canEdit && !empValidated ? 'hover:bg-slate-100' : ''
                                  } ${clipboardSlot && canEdit && !empValidated ? 'bg-violet-50 hover:bg-violet-100' : ''}`}
                                >
                                  {clipboardSlot && canEdit && !empValidated ? (
                                    <div className="flex items-center gap-1">
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handlePaste(empData.employee_id, date); }}
                                        className="p-0.5 rounded hover:bg-violet-200 transition-colors"
                                        title="Coller l'émargement"
                                      >
                                        <ClipboardPaste className="h-3.5 w-3.5 text-violet-500" />
                                      </button>
                                    </div>
                                  ) : canEdit && !empValidated ? (
                                    <Plus className="h-3.5 w-3.5 text-slate-300" />
                                  ) : (
                                    <span className="text-slate-200">-</span>
                                  )}
                                </div>
                              </PopoverTrigger>
                              {isEditing && (
                                <PopoverContent className="w-80 p-3" align="start">
                                  <EmargementSlotEditor
                                    form={cellForm}
                                    setForm={setCellForm}
                                    onSave={handleSaveSlot}
                                    onDelete={null}
                                    onCopy={null}
                                    isExisting={false}
                                    absenceTypes={absenceTypes}
                                    planned={null}
                                  />
                                </PopoverContent>
                              )}
                            </Popover>
                          )}
                        </td>
                      );
                    })}
                    {/* Heures column */}
                    <td className="px-2 py-1 text-center font-medium bg-slate-50 border-r">
                      <div>{formatHours(empData.total_heures_realisees || 0)}</div>
                      <div className="text-[10px] text-slate-400">/ {formatHours(empData.total_heures_planifiees || 0)}</div>
                    </td>
                    {/* Validation column */}
                    <td className="px-2 py-1 text-center bg-slate-50">
                      {canEdit ? (
                        empValidated ? (
                          <Button size="sm" variant="outline" className="text-green-700 border-green-300 h-7 text-xs" onClick={() => handleValidateWeek(empData.employee_id, false)}>
                            <CheckCircle2 className="h-3 w-3 mr-1" /> Validé
                          </Button>
                        ) : (
                          <Button size="sm" className="bg-[#0F5156] hover:bg-[#0a3d41] h-7 text-xs" onClick={() => handleValidateWeek(empData.employee_id, true)}>
                            <Check className="h-3 w-3 mr-1" /> Valider
                          </Button>
                        )
                      ) : (
                        empValidated ? (
                          <Badge className="bg-green-100 text-green-800"><CheckCircle2 className="h-3 w-3 mr-1" />Validé</Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-600">En attente</Badge>
                        )
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

// --- Inline slot editor component ---
function EmargementSlotEditor({ form, setForm, onSave, onDelete, onCopy, isExisting, absenceTypes, planned }) {
  const showTimes = form.statut === 'present' || form.statut === 'retard';
  const showAbsenceType = form.statut === 'absent_justifie' || form.statut === 'conge';
  const heuresEffectives = showTimes ? calcEffective(form.heure_arrivee, form.heure_depart, form.pause_minutes) : 0;

  return (
    <div className="space-y-3">
      {/* Planning reference */}
      {planned && planned.type !== 'absence' && (
        <div className="bg-slate-50 border border-slate-200 rounded px-2 py-1.5 text-xs text-slate-500 flex items-center gap-1.5">
          <Clock className="h-3 w-3 text-slate-400" />
          <span>Planifié : {planned.heure_debut} - {planned.heure_fin}{planned.pause_minutes ? ` (${planned.pause_minutes}min pause)` : ''}</span>
          {planned.poste && <span className="text-slate-400">({planned.poste})</span>}
        </div>
      )}

      {/* Statut selector */}
      <div>
        <Label className="text-xs">Statut</Label>
        <Select value={form.statut} onValueChange={v => setForm(prev => ({ ...prev, statut: v }))}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            {statutOptions.map(s => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Time fields for present/retard */}
      {showTimes && (
        <>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Arrivée</Label>
              <Input
                type="time" value={form.heure_arrivee}
                onChange={e => setForm(prev => ({ ...prev, heure_arrivee: e.target.value }))}
                className="h-8 text-xs"
              />
            </div>
            <div>
              <Label className="text-xs">Départ</Label>
              <Input
                type="time" value={form.heure_depart}
                onChange={e => setForm(prev => ({ ...prev, heure_depart: e.target.value }))}
                className="h-8 text-xs"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Pause (minutes)</Label>
            <Input
              type="number" min="0" step="5" value={form.pause_minutes || 0}
              onChange={e => setForm(prev => ({ ...prev, pause_minutes: parseInt(e.target.value) || 0 }))}
              className="h-8 text-xs"
            />
          </div>
          <div className="bg-slate-50 rounded px-2 py-1 text-center">
            <span className="text-xs text-slate-500">Heures effectives : </span>
            <span className="text-sm font-semibold text-[#0F5156]">{formatHours(heuresEffectives)}</span>
          </div>
        </>
      )}

      {/* Absence type */}
      {showAbsenceType && (
        <div>
          <Label className="text-xs">Type d'absence</Label>
          <Select value={form.type_absence} onValueChange={v => setForm(prev => ({ ...prev, type_absence: v }))}>
            <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Type d'absence" /></SelectTrigger>
            <SelectContent>
              {absenceTypes.map(t => (
                <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Commentaire */}
      <div>
        <Label className="text-xs">Commentaire</Label>
        <Input
          value={form.commentaire}
          onChange={e => setForm(prev => ({ ...prev, commentaire: e.target.value }))}
          placeholder="Commentaire..."
          className="h-8 text-xs"
        />
      </div>

      {/* Actions */}
      <div className="flex gap-1.5 pt-1">
        <Button size="sm" onClick={onSave} className="flex-1 h-7 text-xs bg-[#0F5156] hover:bg-[#0a3d41]">
          Enregistrer
        </Button>
        {isExisting && onCopy && (
          <Button size="sm" variant="outline" onClick={onCopy} className="h-7 text-xs" title="Copier">
            <Copy className="h-3 w-3" />
          </Button>
        )}
        {isExisting && onDelete && (
          <Button size="sm" variant="destructive" onClick={onDelete} className="h-7 text-xs" title="Supprimer">
            <Trash2 className="h-3 w-3" />
          </Button>
        )}
      </div>
    </div>
  );
}
